import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';

import type { DashboardParam } from '../types.js';

dayjs.extend(utc);

const ROLLING_RE = /^(\d+)([dmy])$/;
const CALENDAR_PRESETS = new Set(['today', 'this_week', 'this_month', 'this_year']);

// Per-unit sanity ceilings (not just overflow guards — dayjs handles far larger values without
// throwing): the largest window we let an author lock. 3650d ≈ 10y, 120m = 10y, 100y.
// Anything beyond is almost certainly a typo, and capping keeps the resolved window finite.
const MAX_ROLLING: Record<string, number> = { d: 3650, m: 120, y: 100 };

const ROLLING_UNIT: Record<string, dayjs.ManipulateType> = {
  d: 'day',
  m: 'month',
  y: 'year',
};

const ROLLING_LABELS: Record<string, [string, string]> = {
  d: ['day', 'days'],
  m: ['month', 'months'],
  y: ['year', 'years'],
};

const CALENDAR_LABELS: Record<string, string> = {
  today: 'Today',
  this_week: 'This week',
  this_month: 'This month',
  this_year: 'This year',
};

/** Decorative labels from before `default` was parsed as preset grammar. */
const LEGACY_PRESET_ALIASES: Record<string, string> = {
  last_7_days: '7d',
  last_30_days: '30d',
  last_90_days: '90d',
};

/** Map a persisted legacy label to the new preset grammar; pass through otherwise. */
export function normalizePreset(preset: string): string {
  return LEGACY_PRESET_ALIASES[preset] ?? preset;
}

type PresetDateRange = { start: string; end: string };

type RollingParsed = { n: number; unitKey: string };

// Split a rolling preset ("7d") into { n, unitKey }; null when it isn't a rolling duration.
function parseRolling(preset: string): RollingParsed | null {
  const m = ROLLING_RE.exec(preset);
  if (!m) return null;
  return { n: parseInt(m[1] ?? '0', 10), unitKey: m[2] ?? '' };
}

// Returns an error message for an invalid preset, or null when valid.
export function validateFixedPreset(preset: string): string | null {
  preset = normalizePreset(preset);
  if (CALENDAR_PRESETS.has(preset)) return null;
  const parsed = parseRolling(preset);
  if (!parsed) {
    return `"${preset}" is not valid — use a rolling duration like "7d", "30d", "3m", "1y" or a calendar preset: today, this_week, this_month, this_year`;
  }
  const { n, unitKey } = parsed;
  // Zero-magnitude presets (0d, 0m, …) make a zero-width window that silently returns no rows.
  if (n < 1) {
    return `"${preset}" must be at least 1${unitKey}`;
  }
  const max = MAX_ROLLING[unitKey];
  if (max !== undefined && n > max) {
    return `"${preset}" exceeds maximum of ${String(max)}${unitKey}`;
  }
  return null;
}

function calendarStart(preset: string, d: dayjs.Dayjs): dayjs.Dayjs {
  switch (preset) {
    case 'today':
      return d.startOf('day');
    case 'this_week':
      // Pin to Sunday explicitly. dayjs's startOf('week') reads the mutable global
      // locale's weekStart (a future dayjs.locale()/updateLocale could shift it to
      // Monday and silently change persisted dashboards). d.day() is locale-independent
      // (0 = Sunday), so subtracting it always lands on the preceding Sunday.
      return d.subtract(d.day(), 'day').startOf('day');
    case 'this_month':
      return d.startOf('month');
    case 'this_year':
      return d.startOf('year');
    default:
      return d;
  }
}

// 'm' = month (not minute). Week starts on Sunday. Every window spans whole UTC days:
// start = midnight of the start day, end = 23:59:59.999Z of today. All values are ISO 8601 UTC.
export function presetToDateRange(preset: string, now: Date): PresetDateRange {
  preset = normalizePreset(preset);
  const d = dayjs.utc(now);

  // Calendar presets are always valid and don't go through the rolling parser.
  if (CALENDAR_PRESETS.has(preset)) {
    return { start: calendarStart(preset, d).toISOString(), end: d.endOf('day').toISOString() };
  }

  const parsed = parseRolling(preset);
  const err = validateFixedPreset(preset);
  if (err || !parsed) throw new Error(`Invalid preset: ${err ?? 'invalid preset'}`);

  const unit = ROLLING_UNIT[parsed.unitKey] ?? 'day';
  const start = d.subtract(parsed.n, unit);
  return { start: start.startOf('day').toISOString(), end: d.endOf('day').toISOString() };
}

/**
 * True when `value` is a strict `YYYY-MM-DD` real calendar date. dayjs's lenient parser rolls a
 * non-real date like "2026-13-45" over to a valid-but-wrong one, so round-trip the components and
 * reject any that don't match. The single date-only validity contract shared by the SQL layer and
 * the dashboard param bar (see `dashboard-params.ts`).
 */
export function isValidDateOnly(value: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return false;
  const [y, mo, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = dayjs.utc(Date.UTC(y, mo - 1, day));
  return d.year() === y && d.month() + 1 === mo && d.date() === day;
}

/**
 * Expand a date-only `YYYY-MM-DD` value to an inclusive whole-day UTC instant — `00:00:00.000Z` for
 * a start bound, `23:59:59.999Z` for an end bound. The single definition of "whole UTC day" shared
 * by the SQL substitution layer (`expandTimeRangeBound`) and the preset math above. A value that
 * isn't a real date (e.g. "2026-13-45") is returned unchanged so downstream binding can reject it.
 */
export function dateOnlyToBound(value: string, isEnd: boolean): string {
  if (!isValidDateOnly(value)) return value;
  const [y, mo, day] = value.split('-').map(Number) as [number, number, number];
  const d = dayjs.utc(Date.UTC(y, mo - 1, day));
  return (isEnd ? d.endOf('day') : d.startOf('day')).toISOString();
}

export function formatPresetLabel(preset: string): string {
  preset = normalizePreset(preset);
  const parsed = parseRolling(preset);
  if (parsed) {
    const [singular, plural] = ROLLING_LABELS[parsed.unitKey] ?? ['day', 'days'];
    return `Last ${parsed.n} ${parsed.n === 1 ? singular : plural}`;
  }
  return CALENDAR_LABELS[preset] ?? preset;
}

/**
 * Overwrite `{name}_start` / `{name}_end` for every fixed_preset time_range param with its locked
 * window, discarding client-supplied bounds — the single enforcement point that stops a crafted
 * request (incl. anonymous share-token viewers) from widening the window. Mutates and returns
 * `values`. Throws on a malformed persisted preset; untrusted callers must catch.
 */
export function applyFixedPresets(
  values: Record<string, string | string[]>,
  parameters: readonly DashboardParam[],
  now: Date,
): Record<string, string | string[]> {
  for (const param of parameters) {
    if (param.type === 'time_range' && param.fixed_preset) {
      const { start, end } = presetToDateRange(param.fixed_preset, now);
      values[`${param.name}_start`] = start;
      values[`${param.name}_end`] = end;
    }
  }
  return values;
}

/**
 * Resolve an adjustable time_range's initial window as YYYY-MM-DD bounds. Honors a `default`
 * preset (with legacy alias support); falls back to the last 30 days.
 */
export function resolveAdjustableTimeRangeDefault(
  rawDefault: string | string[] | undefined,
  now: Date,
): { start: string; end: string } {
  if (typeof rawDefault === 'string') {
    try {
      const { start, end } = presetToDateRange(rawDefault, now);
      return { start: start.slice(0, 10), end: end.slice(0, 10) };
    } catch {
      // Unrecognized default — fall through to the 30-day fallback.
    }
  }
  const { start, end } = presetToDateRange('30d', now);
  return { start: start.slice(0, 10), end: end.slice(0, 10) };
}
